import qs from 'query-string';
import {
  defaultTo,
  filter,
  find,
  keys,
  keysIn,
  mergeWith,
  pick,
  pickBy,
  pipe,
  reduce,
  values,
} from 'ramda';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectTenant, Tenant } from 'state/tenant';
import useReactRouter from 'use-react-router';
import useReferrerQueryParameters from 'hooks/useReferrerQueryParameters';

// **************************** ACHTUNG! ************************
//   When adding query parameters of boolean type,
//   you must also add the field to `parseBlanksAsBoolean` below!
// **************************************************************

export interface QueryParameters {
  /*
   * # Workflows:
   *
   * Workflows define the path by which a consumer reaches an exit page.
   *
   * These workflows are made up of the steps it takes to apply for or look
   * up an credit account.
   *
   * Ideally, the exit page is the DSP (digital shopping pass); however, the
   * exit page may also be an error or existing-account page.
   *
   * # Summary of Workflows:
   *
   * ## phoneLookup (aka Frictionless Web)
   *
   * Entry Path: /
   * Mock Params: mockSOTP, mockVOTP, mockCS, mockIC
   *
   * ## tokenLookup (aka FMC, Frictionless Mobile Credit, in-store)
   *
   * Entry Path: /token=[token]
   * Mock Params: mockSOTP, mockVOTP, mockCS, mockIC
   *
   * *Note*: token is required q param. If not included, worflow will be
   * interpreted as phoneLookup.
   *
   * ## dspPush
   *
   * Entry Path: /dsp-start?token=[token]
   * Mock Params: mockDspPush
   *
   * ## prescreen
   *
   * Entry Path: /prescreen/offer?prescreenId=[prescreenId]
   * Mock Params: mockMO, mockPA
   *
   * ## batchPrescreen
   *
   * Entry Path: /batch-prescreen/start
   * Mock Params: mockVL, mockPA, mockVOTP, mockSOTP, mockCS, mockIC
   *
   * Note: allows fallback to IC if prescreenId bad/expired
   *
   * ## prequalTokenLookup
   *
   * Entry Path: /prequal/token-lookup?token=[token]
   * Mock Params: mockCS, mockPrequal, mockPrequalApp, mockIC
   *
   * Note: allows fallback to IC if prequal is notQualified
   *
   * ## prequalPhoneLookup
   *
   * Entry Path: /prequal/phone-lookup
   * Mock Params: mockSOTP, mockVOTP, mockCS, mockPrequal, mockPrequalApp, mockIC
   *
   * Note: allows fallback to IC if prequal is notQualified
   *
   * ## prequalOfferPush
   *
   * Entry Path: /prequal/offer-push?prequalificationId=[prequalificationId]
   * Mock Params: mockVL, mockSOTP, mockVOTP, mockCS, mockPrequal, mockPrequalApp, mockIC
   *
   * Note: allows fallback to IC if prequal is notQualified
   *
   * ## enhancedCheckoutPhoneLookup
   *
   * Entry Path: /enhanced-checkout
   * Mock Params: mockSOTP, mockVOTP, mockCS, mockIC
   *
   * Note: allows fallback to IC if account not found
   *
   * ## enhancedCheckoutTokenLookup
   *
   * Entry Path: /enhanced-checkout?token=[token]
   * Mock Params: mockCS, mockIC
   *
   * Note: allows fallback to IC if account not found
   *
   */

  /**
   * Name used to determine the current tenant.
   */
  clientName: string | undefined;

  /**
   * Whether redirect to root
   */
  redirectToRoot: boolean;

  /*
   * Mock response from consumer search service.
   */
  mockCS:
    | 'success'
    | 'existingAccount'
    | 'successNoToken'
    | 'error'
    | undefined;

  /*
   * Mock response from unified prepare checkout service.
   */
  mockUPC: 'success' | 'error' | undefined;

  /*
   * Mock response from unified consumer search service.
   */
  mockUCS: 'success' | 'error' | undefined;

  /*
   * Mock response from consumer search service.
   */
  mockECO:
    | 'success'
    | 'existingAccount'
    | 'existingAccountWithRewards'
    | 'existingAccountWithPermanentWallets'
    | 'existingAccountWithPermanentWalletsAndRewards'
    | 'error'
    | undefined;

  /*
   * Mock response from drivers license service.
   */
  mockDL:
    | 'success'
    | 'existingAccount'
    | 'successNoToken'
    | 'error'
    | undefined;

  /**
   * Mock a dsp retrieval response
   */
  mockDspRetrieval:
    | 'success'
    | 'existingAccount'
    | 'successNoToken'
    | 'error'
    | undefined;

  /*
   * Mock a dsp push response
   */
  mockDspPush:
    | 'success'
    | 'existingAccount'
    | 'existingAccountPl'
    | 'existingAccountCb'
    | 'successNoToken'
    | 'error'
    | undefined;

  /*
   * Mock response from one time password sender service.
   */
  mockSOTP: 'success' | 'error' | undefined;

  /*
   * Mock response from one time password verifcation service.
   */
  mockVOTP: 'success' | 'error' | undefined;

  /*
   * Mock response from prescreen Application.
   */
  mockPA: 'success' | 'error' | 'existingAccount' | 'denial' | undefined;

  /*
   * Mock response from instant credit service.
   */
  mockIC:
    | 'success'
    | 'successCb'
    | 'existingAccount'
    | 'existingAccountCb'
    | 'existingAccountNonTsp'
    | 'existingAccountMinimal'
    | 'notBestApr'
    | 'nonTsp'
    | 'denial'
    | 'error'
    | undefined;

  /*
   * Mock response from Made Offer service.
   */
  mockMO: 'success' | 'error' | undefined;

  /*
   * Mock response from Virtual Lookup service.
   */
  mockVL: 'success' | 'error' | 'existingAccount' | 'expired' | undefined;

  /*
   * Mock response from Text to recover service
   */
  mockTTR: 'success' | 'error' | undefined;

  /*
   * Mock response from prequal offer text service
   */
  mockPOT: 'success' | 'error' | undefined;

  /**
   *  Mock response from Membership Validation
   */
  mockMV:
    | 'existingAccount'
    | 'error'
    | 'notValidated'
    | 'success'
    | 'successN'
    | 'successD'
    | undefined;

  /**
   * Mock prequalification
   */
  mockPrequal:
    | 'success'
    | 'existingAccount'
    | 'existingPreapproval'
    | 'notQualified'
    | 'denial'
    | 'error'
    | undefined;

  /**
   * Mock response from prequal application
   */
  mockPrequalApp: 'success' | 'error' | undefined;

  /**
   * Mock response for store attribution
   */
  mockStoreAttribution: 'success' | 'error' | undefined;

  /**
   * add productChoiceKey to skip product choice page
   */
  productChoiceKey: string | undefined;

  /**
   * add card choice code to skip card choice page
   */
  cardChoiceCode: number | undefined;

  /**
   * Selected card key
   */
  selectedCardKey: string | undefined;

  /**
   * Default selected card key
   */
  defaultSelectedCardKey: string | undefined;

  /**
   * Mock response for push provision
   */
  mockPushProvision: 'success' | 'error' | undefined;

  /*
   * Mock unified offers
   */
  mockUO: 'success' | 'error' | undefined;

  /**
   * Mock response for JWTToken
   */
  mockUOLookup: 'success' | 'error' | undefined;

  /*
   * Mock unified initial offers
   */
  mockUIO: 'success' | 'error' | undefined;

  /** Mock response for unifiedBuyer
   *
   */
  mockBuyer: 'success' | 'error' | undefined;

  /**
   * Mock response for Unified Disclosures
   */
  mockUD: 'success' | 'error' | undefined;

  /**
   * Mock response for Unified checkout
   */
  mockUC:
    | 'success'
    | 'error'
    | 'successWithFraudAlert'
    | 'successWithFraudAlertOfac'
    | 'successWithFraudAlert'
    | 'successWithFraudFailed'
    | 'successWithKycFailed'
    | 'successWithSplitpayAuthFailedRetry'
    | 'successWithSplitpayAuthFailed'
    | undefined;

  /**
   * Mock response for BraintreeTokens
   */
  mockBraintreeTokens: 'success' | 'error' | undefined;

  mockBraintreeTokenize: 'success' | 'error' | undefined;

  /*
   * Mock response from virtual card issuance
   */
  mockVCI: 'success' | 'error' | undefined;

  /*
   * Mock response from financing google wallet provision
   */
  mockFGWP: 'success' | 'error' | undefined;

  /*
   * Mock response from financing apple wallet provision
   */
  mockFAWP: 'success' | 'error' | undefined;

  /*
   * Mock response from virtual card issuance
   */
  mockVCIS: 'success' | 'error' | undefined;

  /*
   * Mock response from text to recover financing
   */
  mockTTRF: 'success' | 'error' | undefined;

  /*
   * Mock response from text me my offers financing
   */
  mockTMMOF: 'success' | 'error' | undefined;

  /*
   * Mock response from magicLink auth
   */
  mockMagicLinkAuth: 'success' | 'error' | undefined;

  /*
   * Mock resposne for magic-link acceptance
   */
  mockMagicLinkAccept: 'success' | 'error' | undefined;

  /*
   * Mock response from magicLink verify
   */
  mockMagicLinkVerify: 'success' | 'error' | undefined;

  /*
   * Mock response from magiclink status
   */
  mockMagicLinkStatus: 'success' | 'error' | undefined;
  /*
   * Mock response from account data
   */
  mockAccountCardType: 'accountCardType' | undefined;
  mockAccountLogo: 'accountLogo' | undefined;
  mockAccountDivisionNumber: 'accountDivisionNumber' | undefined;

  /**
   * Timeout and warning time in seconds
   */
  instantCreditTimeoutTime: number | undefined;
  instantCreditWarningTime: number | undefined;
  applicationTimeoutTime: number | undefined;
  applicationWarningTime: number | undefined;

  /*
   * Params for use with consumer search, provided by Vibes sms service.
   */
  carrier: string | undefined;
  keyword: string | undefined;
  shortCode: string | undefined;
  token: string | undefined;

  cardType: string | undefined;
  storeNumber: string | undefined;
  prescreenId: string | undefined;
  prequalificationId: string | undefined;
  financingBuyerId: string | undefined;
  loyaltyNumber: string | undefined;
  employeeApplication: boolean;
  embedded: boolean;
  cmp: string | undefined;
  workflow: WorkflowEnum | undefined;

  location: string | undefined;
  channel: string | undefined;
  subchannel: string | undefined;
  applicationSubType: string | undefined;

  echoSessionId: string | undefined;
  echoUrl: string | undefined;
  apprlCallBackUrl: string | undefined;
  nonApprlCallbackUrl: string | undefined;

  /*
   * Params for use with offer pages
   */
  firstOffers: FirstOffersEnum | undefined;

  /**
   * Credit Karma PoC
   */
  subId: string | undefined;

  departmentId: string | undefined;

  /**
   * Flag for if customer already has an existing account
   */
  hasExistingAccount: boolean;

  /**
   * In store
   */
  inStore: boolean;

  clientVariable1: string | undefined;
  clientVariable2: string | undefined;
  clientVariable3: string | undefined;
  clientVariable4: string | undefined;

  epId: string | undefined;

  /**
   * Kiosk mode
   */
  kioskMode: boolean;

  /**
   *  Clerk Mode - special mode for associate access.
   */
  clerkMode: boolean;

  financingLocationId: string | undefined;

  /**
   *  Override Key - used to override values in tenant along with adhocOverrides
   */
  overrideKey: string | undefined;

  /**
   * Magic Link - used to prove verification
   */
  magicLinkId: string | undefined;
  magicLinkOtp: string | undefined;

  /**
   *  vfp
   */
  vfp: string | undefined;
}

export enum WorkflowEnum {
  dspPush = 'dspPush',
  prescreen = 'prescreen',
  batchPrescreen = 'batchPrescreen',
  prequalTokenLookup = 'prequalTokenLookup',
  prequalPhoneLookup = 'prequalPhoneLookup',
  prequalOfferPush = 'prequalOfferPush',
  enhancedCheckoutPhoneLookup = 'enhancedCheckoutPhoneLookup',
  enhancedCheckoutTokenLookup = 'enhancedCheckoutTokenLookup',
  tokenLookup = 'tokenLookup',
  phoneLookup = 'phoneLookup',
  membershipValidation = 'membershipValidation',
  icDriverLicense = 'icDriverLicense',
  prequalDriverLicense = 'prequalDriverLicense',
  unifiedPrequal = 'unifiedPrequal',
  unifiedPrequalCheckout = 'unifiedPrequalCheckout',
}

export enum FirstOffersEnum {
  installments = 'installments',
  splitPay = 'splitpay',
  prequalification = 'prequalification',
}

const DEFAULT_PARAMETERS: QueryParameters = {
  clientName: undefined,
  redirectToRoot: true,
  mockCS: undefined,
  mockUCS: undefined,
  mockECO: undefined,
  mockDL: undefined,
  mockDspRetrieval: undefined,
  mockDspPush: undefined,
  mockSOTP: undefined,
  mockVOTP: undefined,
  mockIC: undefined,
  mockPA: undefined,
  mockMV: undefined,
  mockMO: undefined,
  mockVL: undefined,
  mockTTR: undefined,
  mockPOT: undefined,
  mockUPC: undefined,
  mockVCIS: undefined,
  departmentId: undefined,
  mockPushProvision: undefined,
  mockPrequal: undefined,
  mockPrequalApp: undefined,
  mockStoreAttribution: undefined,
  mockUO: undefined,
  mockUOLookup: undefined,
  mockUIO: undefined,
  mockBuyer: undefined,
  productChoiceKey: undefined,
  cardChoiceCode: undefined,
  selectedCardKey: undefined,
  defaultSelectedCardKey: undefined,
  mockBraintreeTokens: undefined,
  mockBraintreeTokenize: undefined,
  instantCreditTimeoutTime: undefined,
  instantCreditWarningTime: undefined,
  applicationTimeoutTime: undefined,
  applicationWarningTime: undefined,
  carrier: undefined,
  keyword: undefined,
  shortCode: undefined,
  token: undefined,
  cardType: undefined,
  storeNumber: undefined,
  prescreenId: undefined,
  prequalificationId: undefined,
  financingBuyerId: undefined,
  loyaltyNumber: undefined,
  employeeApplication: false,
  embedded: false,
  cmp: undefined,
  workflow: undefined,
  location: undefined,
  channel: undefined,
  subchannel: undefined,
  firstOffers: undefined,
  applicationSubType: undefined,
  echoSessionId: undefined,
  echoUrl: undefined,
  apprlCallBackUrl: undefined,
  nonApprlCallbackUrl: undefined,
  subId: undefined,
  hasExistingAccount: false,
  inStore: false,
  clientVariable1: undefined,
  clientVariable2: undefined,
  clientVariable3: undefined,
  clientVariable4: undefined,
  epId: undefined,
  kioskMode: false,
  clerkMode: false,
  mockUD: undefined,
  mockUC: undefined,
  financingLocationId: undefined,
  mockVCI: undefined,
  mockTTRF: undefined,
  mockTMMOF: undefined,
  mockFGWP: undefined,
  mockFAWP: undefined,
  magicLinkId: undefined,
  magicLinkOtp: undefined,
  vfp: undefined,
  mockMagicLinkVerify: undefined,
  mockMagicLinkAuth: undefined,
  mockMagicLinkAccept: undefined,
  mockMagicLinkStatus: undefined,
  overrideKey: undefined,
  mockAccountCardType: undefined,
  mockAccountLogo: undefined,
  mockAccountDivisionNumber: undefined,
};

type QueryParameterHook = () => QueryParameters;

const canonicalKeyNames = keysIn(
  DEFAULT_PARAMETERS,
) as (keyof QueryParameters)[];

const matchCanonicalKey = (name: string, keyNames: readonly string[]) =>
  find(
    canon => canon.toLowerCase() === name.toLowerCase(),
    keyNames as string[],
  );

const normalizedKeys = <T>(
  keyNames: readonly Extract<keyof T, string>[],
) => (queryParams: { [name: string]: unknown }) => {
  return reduce(
    (accum, name) => {
      const key = matchCanonicalKey(name, keyNames) || name;
      return { ...accum, [key]: queryParams[name] };
    },
    {} as T,
    keysIn(queryParams),
  );
};

const parseNumbers = <K extends string>(fields: K[]) => <
  T extends { [key in K]: unknown }
>(
  query: T,
): { [key in keyof T]: key extends K ? number | undefined : T[key] } => {
  return reduce(
    (acc, field) => {
      const value = query[field];
      if (!value) {
        return acc;
      }
      if (typeof value === 'string') {
        return {
          ...acc,
          [field]: parseFloat(value),
        };
      }
      throw new Error(`cannot cast ${field}=${value} to number`);
    },
    query,
    fields,
    // Typing for reduce sucks!
  ) as { [key in keyof T]: key extends K ? number | undefined : T[key] };
};

const parseBlanksAsBoolean = <K extends string>(fields: K[]) => <
  T extends { [key in K]: unknown }
>(
  query: T,
): { [key in keyof T]: key extends K ? boolean | undefined : T[key] } => {
  return reduce(
    (acc, field) => {
      if (query[field] === undefined || typeof query[field] === 'boolean') {
        // if the query param was not provided, or already parsed properly,
        // we don't have to do anything
        return acc;
      } else {
        // If the query param is present, but null (does not have a value) or
        // is any other value beside the parsable 'true' or 'false', we consider
        // it to be true.
        return {
          ...acc,
          [field]: true,
        };
      }
    },
    query,
    fields,
    // Typing for reduce sucks!
  ) as { [key in keyof T]: key extends K ? boolean : T[key] };
};

interface TranslatedVibesParams {
  carrier?: string;
  keyword?: string;
  shortCode?: string;
  token?: string;
}

const translateVibesParams = (vibesParams: {
  [key: string]: unknown;
}): TranslatedVibesParams => {
  const normalized = normalizedKeys([
    'clientVariable1',
    'clientVariable2',
    'clientVariable3',
    'clientVariable4',
    'sessionToken',
  ] as const)(vibesParams);

  return {
    carrier: normalized['clientVariable1'],
    keyword: normalized['clientVariable2'],
    shortCode: normalized['clientVariable3'],
    token: normalized['sessionToken'],
  };
};

const activeWorkflow = (
  overrideConfig: Tenant['overrideConfig'],
): WorkflowEnum => {
  const keys = values(WorkflowEnum);
  const activeWorkflow = find(key => overrideConfig[key] === true, keys);
  if (!activeWorkflow) {
    // This should not happen. We expect this to have been caught by the
    // server if no active workflow can be determined.
    throw new Error('Tenant does not specify an active workflow.');
  }
  return activeWorkflow;
};

/**
 * Return an object of **only** supported query parameters by the app.
 * Each field is already parsed into its correct type.
 */
const useQueryParameters: QueryParameterHook = () => {
  const { location } = useReactRouter();
  const referredParameters = useReferrerQueryParameters();

  const { overrideConfig } = useSelector(selectTenant);
  const params = useMemo(() => {
    const originalQuery = qs.parse(location.search, {
      parseBooleans: true,
    });
    const query = { ...referredParameters, ...originalQuery };
    const parsedParams = pipe(
      normalizedKeys(canonicalKeyNames),
      parseBlanksAsBoolean([
        'embedded',
        'hasExistingAccount',
        'inStore',
        'clerkMode',
        'kioskMode',
      ]),
      parseNumbers([
        'instantCreditTimeoutTime',
        'instantCreditWarningTime',
        'applicationTimeoutTime',
        'applicationWarningTime',
      ]),
      mergeWith(defaultTo, translateVibesParams(query)),
      pick((keys(DEFAULT_PARAMETERS) as unknown) as string[]),
      mergeWith(defaultTo, DEFAULT_PARAMETERS),
    )(query);

    const params = {
      ...parsedParams,
      workflow: activeWorkflow(overrideConfig),
    };
    return params;
  }, [location, overrideConfig, referredParameters]);
  return params;
};

type CondensedQueryParameterHook = () => Partial<QueryParameters>;
/**
 * Same as useQueryParameters, but condensed to only field that are
 * different from the default. Useful for setting the query parameters in a URL.
 */
const useCondensedQueryParameters: CondensedQueryParameterHook = () => {
  const params = useQueryParameters();

  return useMemo(
    () =>
      pickBy(
        (value, key) =>
          value !== DEFAULT_PARAMETERS[key as keyof QueryParameters],
        params,
      ),
    [params],
  );
};

type SecureQueryParametersHook = () => Partial<QueryParameters>;
/**
 * Query parameters that are allowed in application and DSP timeouts.
 */
const useSecureQueryParameters: SecureQueryParametersHook = () => {
  const params = useCondensedQueryParameters();

  return useMemo(() => {
    const keepParams = [
      'clientName',
      'token',
      'embedded',
      'workflow',
      'employeeApplication',
      'inStore',
      'loyaltyNumber',
      'carrier',
      'keyword',
      'shortCode',
      'loyaltyNumber',
      'storeNumber',
      'location',
      'channel',
      'subchannel',
      'applicationSubType',
      'departmentId',
      'echoSessionId',
      'echoUrl',
      'apprlCallBackUrl',
      'nonApprlCallbackUrl',
      'clientVariable1',
      'clientVariable2',
      'clientVariable3',
      'clientVariable4',
      'clerkMode',
      'kioskMode',
      'productChoiceKey',
      'cardChoiceCode',
      'defaultSelectedCardKey',
      'selectedCardKey',
      'epId',
      'overrideKey',
      'magicLinkId',
      'magicLinkOtp',
      // The following are kept only for mocking/debugging purpose in development
      'redirectToRoot',
      'applicationTimeoutTime',
      'applicationWarningTime',
      'instantCreditWarningTime',
      'instantCreditTimeoutTime',
      'financingLocationId',
      'cardType',
      'vfp',
      ...filter((s: string) => s.includes('mock'), keysIn(params)),
    ];

    return {
      ...pick(keepParams, params),
    };
  }, [params]);
};

export default useQueryParameters;

export { useCondensedQueryParameters, useSecureQueryParameters };
